import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../../api/client';
import { PageHeader } from '../../components/ui/PageHeader';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../components/ui/Toast';
import {
  Banner,
  labelCls,
  thCls,
  apiMessage,
  cmd,
  useCollectionRole,
  type Step,
} from '../../components/collection/collectionShared';

/**
 * Paramètres de relance — the steps (days after the due date, penalties claimed, blocks orders once
 * sent), the minimum gap, the time of the automatic run, the indemnity and penalty rate, the cash
 * ceiling between professionals, and the automatic run itself. Read-only except for the owner.
 */
interface Policy {
  steps: Step[];
  minGapDays: number;
  hour: string;
  indemnity: number;
  penaltyRate: number;
  cashLimitB2B: number;
  autoDunning: boolean;
}

export default function CollectionSettings() {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const { owner } = useCollectionRole();
  const [p, setP] = useState<Policy | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    apiClient
      .get<Policy>('/collection/settings')
      .then((r) => setP(r.data))
      .catch((e) => showApiError(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!p) return <PageHeader title={t('collection.settings.title')} />;
  const step = (n: number, patch: Partial<Step>) =>
    setP({ ...p, steps: p.steps.map((s, i) => (i === n ? { ...s, ...patch } : s)) });
  const save = async () => {
    setErr('');
    try {
      const r = await apiClient.put<Policy>('/collection/settings', p, cmd());
      setP(r.data);
      push({ tone: 'success', message: t('collection.settings.saved') });
    } catch (e) {
      setErr(apiMessage(t, e));
    }
  };
  const box = 'h-5 w-5';
  return (
    <div>
      <PageHeader
        title={t('collection.settings.title')}
        description={t('collection.settings.description')}
      />
      {!owner && <Banner tone="info">{t('collection.settings.readOnly')}</Banner>}
      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <div className="rounded-lg border bg-card shadow-sm">
          <div className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold">{t('collection.settings.steps')}</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className={thCls}>{t('collection.settings.step')}</th>
                  <th className={thCls}>{t('collection.settings.days')}</th>
                  <th className={thCls}>{t('collection.settings.penalties')}</th>
                  <th className={thCls}>{t('collection.settings.blocks')}</th>
                  <th className={thCls}>{t('collection.settings.enabled')}</th>
                </tr>
              </thead>
              <tbody>
                {p.steps.map((s, n) => (
                  <tr key={s.key} className="border-b last:border-b-0">
                    <td className="px-3 py-2">
                      <Input
                        className="h-8"
                        value={s.label}
                        disabled={!owner}
                        onChange={(e) => step(n, { label: e.target.value })}
                        aria-label={t('collection.settings.step')}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <Input
                        className="h-8 w-24"
                        type="number"
                        min={1}
                        value={s.day}
                        disabled={!owner}
                        onChange={(e) => step(n, { day: Number(e.target.value) })}
                        aria-label={t('collection.settings.days')}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        className={box}
                        checked={s.penalties}
                        disabled={!owner}
                        onChange={(e) => step(n, { penalties: e.target.checked })}
                        aria-label={t('collection.settings.penalties')}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        className={box}
                        checked={s.block}
                        disabled={!owner}
                        onChange={(e) => step(n, { block: e.target.checked })}
                        aria-label={t('collection.settings.blocks')}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        className={box}
                        checked={s.enabled}
                        disabled={!owner}
                        onChange={(e) => step(n, { enabled: e.target.checked })}
                        aria-label={t('collection.settings.enabled')}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="border-t px-4 py-2 text-xs text-muted-foreground">
            {t('collection.settings.blockNote')}
          </p>
        </div>
        <div className="rounded-lg border bg-card shadow-sm">
          <div className="border-b px-4 py-3">
            <h2 className="text-sm font-semibold">{t('collection.settings.rules')}</h2>
          </div>
          <div className="grid gap-3 px-4 py-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className={labelCls}>{t('collection.settings.gap')}</span>
                <Input
                  type="number"
                  min={1}
                  value={p.minGapDays}
                  disabled={!owner}
                  onChange={(e) => setP({ ...p, minGapDays: Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className={labelCls}>{t('collection.settings.hour')}</span>
                <Input
                  type="time"
                  value={p.hour}
                  disabled={!owner}
                  onChange={(e) => setP({ ...p, hour: e.target.value })}
                />
              </label>
              <label className="block">
                <span className={labelCls}>{t('collection.settings.indemnity')}</span>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={p.indemnity}
                  disabled={!owner}
                  onChange={(e) => setP({ ...p, indemnity: Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className={labelCls}>{t('collection.settings.rate')}</span>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={p.penaltyRate}
                  disabled={!owner}
                  onChange={(e) => setP({ ...p, penaltyRate: Number(e.target.value) })}
                />
              </label>
              <label className="block">
                <span className={labelCls}>{t('collection.settings.cash')}</span>
                <Input
                  type="number"
                  min={0}
                  value={p.cashLimitB2B}
                  disabled={!owner}
                  onChange={(e) => setP({ ...p, cashLimitB2B: Number(e.target.value) })}
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className={box}
                checked={p.autoDunning}
                disabled={!owner}
                onChange={(e) => setP({ ...p, autoDunning: e.target.checked })}
              />
              {t('collection.settings.auto')}
            </label>
            <p className="text-xs text-muted-foreground">{t('collection.settings.autoNote')}</p>
            <p className="text-xs text-muted-foreground">{t('collection.settings.rateNote')}</p>
            {err && (
              <p className="text-sm font-semibold text-destructive" role="alert">
                {err}
              </p>
            )}
            {owner && <Button onClick={save}>{t('collection.settings.save')}</Button>}
          </div>
        </div>
      </div>
    </div>
  );
}
